import classNames from 'classnames'
import React, { ChangeEvent, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { bindActionCreators } from 'redux'
import { makeStyles } from 'tss-react/mui'

import { TextField, Theme } from '@mui/material'

import { PairingStageFilterSelect } from '../../../../components/PairingStageFilter'
import {
  updateCorporateFilter,
  updateCorporateStatusFilter,
  updateFinderStagesFilter,
  updateIsValidFilter,
  updateOrganizationFilter,
  updateSearchRequestIdFilter,
} from '../../duck/actions'
import {
  getDealManagerCorporateStatusFilter,
  getDealManagerFinderStagesFilter,
  getDealManagerIsValidFilter,
} from '../../duck/selectors'
import { CorporateStatusSelector } from './CorporateStatusSelector.component'
import { IsValidSelector } from './IsValidSelector.component'

export type PairingFiltersProps = {
  className?: string
}

function usePairingFiltersData() {
  const corporateStatus = useSelector(getDealManagerCorporateStatusFilter)
  const isValid = useSelector(getDealManagerIsValidFilter)
  const finderStagesFilter = useSelector(getDealManagerFinderStagesFilter)
  return {
    corporateStatus,
    isValid,
    finderStagesFilter,
  }
}

function usePairingFiltersHandlers() {
  const dispatch = useDispatch()
  return useMemo(
    () =>
      bindActionCreators(
        {
          onOrganizationSearchUpdate: updateOrganizationFilter,
          onCorporateSearchUpdate: updateCorporateFilter,
          onCorporateStatusFilterUpdate: updateCorporateStatusFilter,
          onSearchRequestIdUpdate: updateSearchRequestIdFilter,
          onIsValidFilterUpdate: updateIsValidFilter,
          onFinderStagesFilterUpdate: updateFinderStagesFilter,
        },
        dispatch
      ),
    [dispatch]
  )
}

export const PairingFilters = React.memo(({ className }: PairingFiltersProps) => {
  const { classes } = useStyles()
  const { corporateStatus, isValid, finderStagesFilter } = usePairingFiltersData()
  const {
    onOrganizationSearchUpdate,
    onCorporateSearchUpdate,
    onCorporateStatusFilterUpdate,
    onSearchRequestIdUpdate,
    onIsValidFilterUpdate,
    onFinderStagesFilterUpdate,
  } = usePairingFiltersHandlers()
  return (
    <div className={classNames(className, classes.root)}>
      <div className={classes.userFilters}>
        <TextField
          placeholder="Space Id"
          onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onSearchRequestIdUpdate(e.target.value)}
          className={classes.field}
        />
        <TextField
          placeholder="Organization name or id"
          onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            onOrganizationSearchUpdate(e.target.value)
          }
          className={classes.field}
        />
      </div>

      <div className={classes.companyFilters}>
        <TextField
          placeholder="Corporate name or id"
          onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onCorporateSearchUpdate(e.target.value)}
          className={classes.field}
        />
        <CorporateStatusSelector corporateStatus={corporateStatus} onChange={onCorporateStatusFilterUpdate} />
      </div>
      <div className={classes.pairingFilters}>
        <PairingStageFilterSelect
          className={classNames(classes.stageFilter, classes.field)}
          value={finderStagesFilter}
          onChange={onFinderStagesFilterUpdate}
        />
        <IsValidSelector isValid={isValid} onChange={onIsValidFilterUpdate} />
      </div>
    </div>
  )
})

PairingFilters.displayName = 'PairingFilters'

const useStyles = makeStyles()(({ spacing }: Theme) => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  userFilters: {
    display: 'flex',
    justifyContent: 'flex-end',
    flex: '1 1 auto',
    width: '100%',
    marginBottom: spacing(),
  },
  companyFilters: {
    display: 'flex',
    justifyContent: 'flex-end',
    flex: '1 1 auto',
    width: '100%',
    marginBottom: spacing(),
  },
  pairingFilters: {
    display: 'flex',
    justifyContent: 'flex-end',
    flex: '1 1 auto',
    width: '100%',
  },
  field: {
    marginLeft: spacing(2),
    minWidth: spacing(24),
  },
  stageFilter: {
    minWidth: spacing(32),
  },
}))
